import { Ability, SkillName, SkillProficiencies, ProficiencyLevel, ClassName } from './types';
import { CLASSES, SKILLS, ABILITY_NAMES } from './dnd-data';

export function abilityModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

export function proficiencyBonus(level: number): number {
  return Math.ceil(level / 4) + 1;
}

export function skillBonus(
  abilityScore: number,
  proficiencyLevel: ProficiencyLevel,
  level: number,
  override: number | null = null
): number {
  // WHY: Check !== null instead of truthy because 0 is a valid override value
  if (override !== null) {
    return override;
  }

  const mod = abilityModifier(abilityScore);
  const prof = proficiencyBonus(level);

  switch (proficiencyLevel) {
    case 'expertise':
      return mod + prof * 2;
    case 'proficient':
      return mod + prof;
    case 'none':
    default:
      return mod;
  }
}

export function isClassSavingThrow(className: ClassName, ability: Ability): boolean {
  return CLASSES[className].savingThrows.includes(ability);
}

export function savingThrow(
  abilityScore: number,
  className: ClassName,
  ability: Ability,
  level: number,
  override: number | null
): number {
  if (override !== null) {
    return override;
  }

  const mod = abilityModifier(abilityScore);
  const prof = proficiencyBonus(level);

  return isClassSavingThrow(className, ability) ? mod + prof : mod;
}

export function calculateSkillBonuses(
  abilityScores: Record<Ability, number>,
  skills: SkillProficiencies,
  level: number,
  // WHY: Optional instead of defaulting to {} because TypeScript strict mode
  // rejects {} as a default for Record<SkillName, number | null>
  skillOverrides?: Record<SkillName, number | null>
): Record<SkillName, number> {
  const result = {} as Record<SkillName, number>;

  for (const skillName of Object.keys(SKILLS) as SkillName[]) {
    const skillData = SKILLS[skillName];
    const abilityScore = abilityScores[skillData.ability];
    const profLevel = skills[skillName] || 'none';
    const override = skillOverrides?.[skillName] ?? null;
    result[skillName] = skillBonus(abilityScore, profLevel, level, override);
  }

  return result;
}

export type SavingThrowOverrides = Record<Ability, number | null>;

export function calculateSavingThrowBonuses(
  abilityScores: Record<Ability, number>,
  className: ClassName,
  level: number,
  // WHY: Optional instead of defaulting to {} because TypeScript strict mode
  // rejects {} as a default for Record<Ability, number | null>
  savingThrowOverrides?: SavingThrowOverrides
): Record<Ability, number> {
  const result = {} as Record<Ability, number>;

  for (const ability of Object.keys(ABILITY_NAMES) as Ability[]) {
    const override = savingThrowOverrides?.[ability] ?? null;
    result[ability] = savingThrow(abilityScores[ability], className, ability, level, override);
  }

  return result;
}

export function parseSkills(skillsJson: string): SkillProficiencies {
  try {
    return JSON.parse(skillsJson);
  } catch {
    return {} as SkillProficiencies;
  }
}
